import { useCallback, useEffect, useState } from 'react';

import { IFeedbackModal } from 'components/layout/FeedbackModal';
import { IOption } from 'components/system/form/FormAutocomplete';
import { displayToast } from 'components/system/Toast';
import { axiosRestApi } from 'config/axiosRestApi';
import { useFeedbackModal } from 'providers';
import { concatPhoneNumber } from 'utils/concatPhoneNumber';
import { getAxiosTooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';
import { AUTH_STEP } from '../AuthManager/constants';
import { useAuthManagerContext } from '../AuthManager/Context/useAuthManagerContext';
import { IPhoneNumber } from '../types';

interface ICountryCallingCodeOption {
  countryCallingCode: string;
  countryCode: string;
}

const registerNewPhoneRequest = async (
  phone: string,
  countryCallingCode: string,
) => {
  return await axiosRestApi.post('/register-new-phone', {
    phone,
    countryCallingCode,
  });
};

const countryCallingCodeRequest = async () => {
  return await axiosRestApi.get<ICountryCallingCodeOption[]>(
    '/countries/calling-codes',
  );
};

const DEFAULT_ERROR_PROPS: IFeedbackModal = {
  type: 'error',
  title: 'Error sending the phone number',
  message: 'Please try again later',
};

export const usePhoneVerification = () => {
  const [loading, setLoading] = useState(false);
  const { setStep, setPhone } = useAuthManagerContext();
  const [codeOptions, setCodeOptions] = useState<IOption[]>([]);
  const { openFeedbackModal } = useFeedbackModal();

  const getCountryCallingCodes = useCallback(async () => {
    try {
      const response = await countryCallingCodeRequest();

      if (!response.data) {
        throw new Error('No country calling codes found');
      }

      const mappedOptions: IOption[] = response.data.map(
        (option: ICountryCallingCodeOption) => ({
          value: `${option.countryCode}-${option.countryCallingCode}`,
          label: `${option.countryCode} +${option.countryCallingCode}`,
        }),
      );

      setCodeOptions(mappedOptions);
    } catch (error: unknown) {
      logError({ error });

      const modalProps =
        getAxiosTooManyRequestsFeedbackModalProps(error) ?? DEFAULT_ERROR_PROPS;

      openFeedbackModal(modalProps);
    } finally {
      setLoading(false);
    }
  }, [openFeedbackModal]);

  useEffect(() => {
    void getCountryCallingCodes();
  }, [getCountryCallingCodes]);

  const handleBackToLogin = () => {
    setStep(AUTH_STEP.DEFAULT);
  };

  const handleFormSubmit = useCallback(
    async (fields: IPhoneNumber) => {
      setLoading(true);
      try {
        const { phone, countryCallingCode } = fields;
        const formattedCountryCallingCode =
          countryCallingCode.value.split('-')[1];
        const phoneWithCallingCode = concatPhoneNumber(
          phone,
          formattedCountryCallingCode,
        );
        await registerNewPhoneRequest(phone, formattedCountryCallingCode);

        setPhone({
          phoneWithCallingCode,
          phone,
          countryCallingCode: formattedCountryCallingCode,
        });
        setStep(AUTH_STEP.TWO_FACTOR_VERIFICATION);
      } catch (error: unknown) {
        logError({ error });
        displayToast({
          type: 'error',
          title: 'Phone registration failed',
          description: 'Something went wrong. Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    },
    [setPhone, setStep],
  );

  return {
    codeOptions,
    handleBackToLogin,
    handleFormSubmit,
    loading,
  };
};
